import React, { useState } from 'react';
import { Icon } from './Icon';
import {
    DEFAULT_EXPORT_COLUMNS,
    DEFAULT_EXPORT_FILTERS,
    type ExportColumn,
    type ExportFilters,
    useExportLeads,
} from '../hooks/useExportLeads';
import type { LeadFilters } from './LeadsSearchFilters';
import type { User } from '../types/lead';

// Igual que los mocks del LeadsSearchFilters — reemplaza por tus hooks reales si los tienes
const SOURCES = [
    { id: '1',  name: 'Facebook'     },
    { id: '2',  name: 'Google Forms' },
    { id: '3',  name: 'Eventos'      },
    { id: '35', name: 'Whatsapp'     },
    { id: '36', name: 'Web'          },
    { id: '37', name: 'Visitas'      },
    { id: '38', name: 'Tik Tok'      },
    { id: '39', name: 'Referidos'    },
];

const STATUSES = [
    { id: '1', name: 'NUEVO'              },
    { id: '2', name: 'CONTACTO EFECTIVO'  },
    { id: '5', name: 'NO CONTACTADO'      },
    { id: '3', name: 'EN ESPERA'          },
    { id: '4', name: 'LOW'                },
    { id: '7', name: 'MEDIUM'             },
    { id: '8', name: 'HIGH'               },
    { id: '6', name: 'CONVERTIDO'         },
];

// Si tienes un hook useProgramas(), úsalo aquí; por ahora prop opcional
interface ExportModalProps {
    isOpen:     boolean;
    onClose:    () => void;
    baseFilters: LeadFilters;
    total:      number;
    users?:     User[];
    programas?: { id: string; name: string }[];
}

type Tab = 'filtros' | 'columnas';

export const ExportModal: React.FC<ExportModalProps> = ({
    isOpen,
    onClose,
    baseFilters,
    total,
    users = [],
    programas = [],
}) => {
    const [activeTab,     setActiveTab]     = useState<Tab>('filtros');
    const [columns,       setColumns]       = useState<ExportColumn[]>(DEFAULT_EXPORT_COLUMNS);
    const [exportFilters, setExportFilters] = useState<ExportFilters>(DEFAULT_EXPORT_FILTERS);

    const { exportToExcel, exporting, exportError } = useExportLeads();

    if (!isOpen) return null;

    const enabledCount  = columns.filter((c) => c.enabled).length;

    // ── helpers ────────────────────────────────────────────────────────────────
    const setFilter = (key: keyof ExportFilters, value: string) =>
        setExportFilters((prev) => ({ ...prev, [key]: value }));

    const toggleColumn = (key: string) =>
        setColumns((prev) =>
            prev.map((col) => (col.key === key ? { ...col, enabled: !col.enabled } : col))
        );

    const toggleAll = () => {
        const allOn = columns.every((c) => c.enabled);
        setColumns((prev) => prev.map((col) => ({ ...col, enabled: !allOn })));
    };

    const resetFilters = () => setExportFilters(DEFAULT_EXPORT_FILTERS);

    const activeExportFilterCount = Object.values(exportFilters).filter(Boolean).length;

    const handleExport = async () => {
        await exportToExcel({ baseFilters, exportFilters, columns });
    };

    // ── select class helper ────────────────────────────────────────────────────
    const selectCls =
        'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg ' +
        'bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' +
        'focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none';

    const inputCls =
        'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg ' +
        'bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' +
        'focus:ring-2 focus:ring-blue-500 focus:border-transparent';

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] animate-fade-in">

                    {/* ── Header ─────────────────────────────────────────────── */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                <Icon name="download" className="w-5 h-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                    Exportar a Excel
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Hasta {total} leads · {enabledCount} columnas
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                            <Icon name="close" className="w-5 h-5" />
                        </button>
                    </div>

                    {/* ── Tabs ───────────────────────────────────────────────── */}
                    <div className="flex border-b border-gray-200 dark:border-gray-700 shrink-0">
                        {(['filtros', 'columnas'] as Tab[]).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 py-2.5 text-sm font-medium transition-colors capitalize relative ${
                                    activeTab === tab
                                        ? 'text-blue-600 dark:text-blue-400'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                {tab === 'filtros' ? 'Segmentar datos' : 'Columnas'}
                                {tab === 'filtros' && activeExportFilterCount > 0 && (
                                    <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                                        {activeExportFilterCount}
                                    </span>
                                )}
                                {tab === 'columnas' && (
                                    <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full">
                                        {enabledCount}
                                    </span>
                                )}
                                {activeTab === tab && (
                                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-t" />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* ── Body (scrollable) ───────────────────────────────────── */}
                    <div className="overflow-y-auto flex-1 px-6 py-5">

                        {/* ════════ TAB: FILTROS ════════ */}
                        {activeTab === 'filtros' && (
                            <div className="space-y-5">

                                {/* Filtros heredados del listado (solo informativo) */}
                                {(baseFilters.searchTerm || baseFilters.agentId || baseFilters.sourceId || baseFilters.statusId) && (
                                    <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 px-3 py-2.5">
                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                            Filtros activos en el listado (incluidos automáticamente)
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {baseFilters.searchTerm && <Chip label={`"${baseFilters.searchTerm}"`} />}
                                            {baseFilters.agentId    && <Chip label="Agente filtrado"  />}
                                            {baseFilters.sourceId   && <Chip label="Fuente filtrada"  />}
                                            {baseFilters.statusId   && <Chip label="Estado filtrado"  />}
                                        </div>
                                    </div>
                                )}

                                {/* Rango de fechas */}
                                <fieldset className="space-y-3">
                                    <legend className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center space-x-1.5">
                                        <Icon name="calendar" className="w-4 h-4 text-gray-400" />
                                        <span>Rango de fechas de creación</span>
                                    </legend>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Desde</label>
                                            <input
                                                type="date"
                                                value={exportFilters.dateFrom}
                                                max={exportFilters.dateTo || undefined}
                                                onChange={(e) => setFilter('dateFrom', e.target.value)}
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hasta</label>
                                            <input
                                                type="date"
                                                value={exportFilters.dateTo}
                                                min={exportFilters.dateFrom || undefined}
                                                onChange={(e) => setFilter('dateTo', e.target.value)}
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                </fieldset>

                                {/* Estado */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 flex items-center space-x-1.5">
                                        <Icon name="tag" className="w-4 h-4 text-gray-400" />
                                        <span>Estado</span>
                                    </label>
                                    <select
                                        value={exportFilters.statusId}
                                        onChange={(e) => setFilter('statusId', e.target.value)}
                                        className={selectCls}
                                    >
                                        <option value="">
                                            {baseFilters.statusId
                                                ? `Usar filtro del listado (${STATUSES.find(s => s.id === baseFilters.statusId)?.name ?? baseFilters.statusId})`
                                                : 'Todos los estados'}
                                        </option>
                                        {STATUSES.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Fuente */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 flex items-center space-x-1.5">
                                        <Icon name="globe" className="w-4 h-4 text-gray-400" />
                                        <span>Fuente</span>
                                    </label>
                                    <select
                                        value={exportFilters.sourceId}
                                        onChange={(e) => setFilter('sourceId', e.target.value)}
                                        className={selectCls}
                                    >
                                        <option value="">
                                            {baseFilters.sourceId
                                                ? `Usar filtro del listado (${SOURCES.find(s => s.id === baseFilters.sourceId)?.name ?? baseFilters.sourceId})`
                                                : 'Todas las fuentes'}
                                        </option>
                                        {SOURCES.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Agente */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 flex items-center space-x-1.5">
                                        <Icon name="user" className="w-4 h-4 text-gray-400" />
                                        <span>Agente</span>
                                    </label>
                                    <select
                                        value={exportFilters.agentId}
                                        onChange={(e) => setFilter('agentId', e.target.value)}
                                        className={selectCls}
                                    >
                                        <option value="">
                                            {baseFilters.agentId ? 'Usar filtro del listado' : 'Todos los agentes'}
                                        </option>
                                        {users.map((u) => (
                                            <option key={u.id} value={u.id}>{u.full_name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Programa de interés */}
                                {programas.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 flex items-center space-x-1.5">
                                            <Icon name="book-open" className="w-4 h-4 text-gray-400" />
                                            <span>Programa de interés</span>
                                        </label>
                                        <select
                                            value={exportFilters.programaId}
                                            onChange={(e) => setFilter('programaId', e.target.value)}
                                            className={selectCls}
                                        >
                                            <option value="">Todos los programas</option>
                                            {programas.map((p) => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Limpiar filtros del export */}
                                {activeExportFilterCount > 0 && (
                                    <button
                                        onClick={resetFilters}
                                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex items-center space-x-1 transition-colors"
                                    >
                                        <Icon name="x" className="w-3.5 h-3.5" />
                                        <span>Limpiar filtros de exportación</span>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* ════════ TAB: COLUMNAS ════════ */}
                        {activeTab === 'columnas' && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Selecciona qué campos aparecerán en el archivo.
                                    </p>
                                    <button
                                        onClick={toggleAll}
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline shrink-0"
                                    >
                                        {columns.every((c) => c.enabled) ? 'Desmarcar todo' : 'Seleccionar todo'}
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {columns.map((col) => (
                                        <label
                                            key={col.key}
                                            className={`flex items-center space-x-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm select-none ${
                                                col.enabled
                                                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                                                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={col.enabled}
                                                onChange={() => toggleColumn(col.key)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 shrink-0"
                                            />
                                            <span className="truncate">{col.label}</span>
                                        </label>
                                    ))}
                                </div>
                                {enabledCount === 0 && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center space-x-1.5">
                                        <Icon name="alert-circle" className="w-4 h-4 shrink-0" />
                                        <span>Selecciona al menos una columna.</span>
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Footer ─────────────────────────────────────────────── */}
                    <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
                        {exportError && (
                            <p className="text-xs text-red-600 dark:text-red-400 flex items-center space-x-1.5 mb-3">
                                <Icon name="alert-circle" className="w-4 h-4 shrink-0" />
                                <span>{exportError}</span>
                            </p>
                        )}
                        <div className="flex items-center justify-end space-x-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleExport}
                                disabled={exporting || enabledCount === 0}
                                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                            >
                                {exporting ? (
                                    <>
                                        <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                        </svg>
                                        Generando…
                                    </>
                                ) : (
                                    <>
                                        <Icon name="download" className="w-4 h-4 mr-2" />
                                        Descargar Excel
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// ── Chip pequeño para mostrar filtros del listado ──────────────────────────────
const Chip: React.FC<{ label: string }> = ({ label }) => (
    <span className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full">
        {label}
    </span>
);